import React from 'react';
import type { ComponentPropsWithoutRef, Ref } from 'react';
import styled from 'styled-components';

import Box from '@ui/box/Box.tsx';
import MapNavButton from '@ui/buttons/MapNavButton.tsx';
import Tooltip from '@ui/tooltip';

/*
 * ZoomControl component
 * - Two vertically stacked buttons: Zoom In / Zoom Out
 * - Accessible group with role="group" and a label
 * - Accepts a ref prop (React 19 ref-as-prop)
 */

const Wrapper = styled(Box)`
  display: inline-block;
`;

type ZoomControlProps = ComponentPropsWithoutRef<typeof Wrapper> & {
  ref?: Ref<HTMLDivElement>;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  zoomInLabel?: string;
  zoomOutLabel?: string;
  zoomInTooltipContent?: string;
  zoomOutTooltipContent?: string;
  zoomInAriaLabel?: string;
  zoomOutAriaLabel?: string;
  groupLabel?: string;
};

const ZoomControl = ({
  ref,
  onZoomIn,
  onZoomOut,
  zoomInLabel = 'Zoom In',
  zoomOutLabel = 'Zoom Out',
  zoomInTooltipContent,
  zoomOutTooltipContent,
  zoomInAriaLabel,
  zoomOutAriaLabel,
  groupLabel = 'Map Zoom Controls',
  ...rest
}: ZoomControlProps) => {
  const zoomInTooltip = zoomInTooltipContent || zoomInLabel;
  const zoomOutTooltip = zoomOutTooltipContent || zoomOutLabel;
  const zoomInAria = zoomInAriaLabel || zoomInTooltipContent || zoomInLabel;
  const zoomOutAria = zoomOutAriaLabel || zoomOutTooltipContent || zoomOutLabel;
  return (
    <Wrapper role="group" aria-label={groupLabel} data-testid="ZoomControl" ref={ref} {...rest}>
      <Box width="30px" height="30px">
        <Tooltip text={zoomInTooltip} arrowPosition="left">
          <MapNavButton
            onClick={onZoomIn}
            iconName="zoom.in"
            $boxShadow="none"
            $borderBottomLeftRadius="0"
            $borderBottomRightRadius="0"
            aria-label={zoomInAria}
          />
        </Tooltip>
      </Box>
      <Box width="30px" height="30px">
        <Tooltip text={zoomOutTooltip} arrowPosition="left">
          <MapNavButton
            onClick={onZoomOut}
            iconName="zoom.out"
            $boxShadow="none"
            $borderTopLeftRadius="0"
            $borderTopRightRadius="0"
            aria-label={zoomOutAria}
          />
        </Tooltip>
      </Box>
    </Wrapper>
  );
};

export default ZoomControl;
